import { PayloadAction } from "@reduxjs/toolkit";
import { IWorkspaceProjects, sortOrder } from "app/types";
import { set } from "lodash";
import { createSlice } from "utils/@reduxjs/toolkit";
import { ITickets, TicketGroup } from "../containers/Tickets/types";
import {
  Board,
  CustomAccessEnum,
  DefaultTicketType,
  IDefaultTicketValueData,
  IVisibleGroups,
} from "../types";
import { defaultSelectedTickets, initialState } from "./initialState";

const formSlice = createSlice({
  name: "boardsState",
  initialState,
  reducers: {
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },

    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsFileUploading: (state, action: PayloadAction<boolean>) => {
      state.isFileUploading = action.payload;
    },
    toggleIsFilePreview: (state, action: PayloadAction<boolean>) => {
      state.isFilePreview = action.payload;
    },
    setFavorites: (state, action: PayloadAction<Board[]>) => {
      state.favorites = action.payload;
    },
    setQaBoards: (state, action: PayloadAction<Board[]>) => {
      state.qaBoards = action.payload;
    },
    setRecents: (state, action: PayloadAction<Board[]>) => {
      state.recent = action.payload;
    },
    setBoardsFormValue: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      set(state, `boardsForm.${action.payload.name}`, action.payload.value);
    },
    setBoardsCustomPermissionValue: (
      state,
      action: PayloadAction<{
        id: string;
        value: CustomAccessEnum;
        index?: number;
      }>,
    ) => {
      if (action.payload.index) {
        state.boardsForm.customPermissions[action.payload.index].access =
          action.payload.value;
      } else {
        state.boardsForm.customPermissions.push({
          id: action.payload.id,
          access: action.payload.value,
        });
      }
    },
    setBoardsGroupVisibility: (state, action: PayloadAction<number>) => {
      state.boardsForm.visibleGroups[action.payload].isVisible =
        !state.boardsForm.visibleGroups[action.payload].isVisible;
    },
    setBoardsGroupAllVisibility: (state, action: PayloadAction<boolean>) => {
      state.boardsForm.visibleGroups.forEach((item) => {
        item.isVisible = action.payload;
      });
    },
    setVisibleGroupOrder: (state, action: PayloadAction<IVisibleGroups[]>) => {
      state.boardsForm.visibleGroups = action.payload;
    },

    setDefaultTicketValue: (
      state,
      action: PayloadAction<{
        id: string;
        value: string;
        type: DefaultTicketType;
        data: IVisibleGroups;
      }>,
    ) => {
      state.boardsForm.defaultTicketValue.push({
        data: action.payload.data,
        id: action.payload.id,
        value: "",
        type: action.payload.type,
      });
    },
    setDefaultTicketValueData: (
      state,
      action: PayloadAction<IDefaultTicketValueData>,
    ) => {
      state.defaultTicketValueData = action.payload;
    },
    setDraftTicket: (state) => {
      const draftId = state.draftTicket.length + 1;
      state.draftTicket.push({
        id: String(draftId),
        name: "",
      });
    },
    removeDraftTicket: (state, action: PayloadAction<{ id: string }>) => {
      const items = state.draftTicket.filter((e) => e.id !== action.payload.id);
      state.draftTicket = items;
    },

    toggleSelectTicket: (
      state,
      action: PayloadAction<{ group: TicketGroup; ticket: ITickets }>,
    ) => {
      const ticket = action.payload.ticket;
      const groupId = action.payload.group._id;
      const isSelected = state.selectedTickets.id.includes(ticket._id);

      if (isSelected) {
        // Unselect the ticket
        state.selectedTickets.id = state.selectedTickets.id.filter(
          (id) => id !== ticket._id,
        );

        // If no other tickets from this group remain selected, remove group
        const groupTicketIds = action.payload.group.ticketsList.map(
          (t) => t._id,
        );
        const stillSelectedFromGroup = groupTicketIds.some((id) =>
          state.selectedTickets.id.includes(id),
        );
        if (!stillSelectedFromGroup) {
          state.selectedTickets.group = state.selectedTickets.group.filter(
            (id) => id !== groupId,
          );
        }

        state.selectedTickets.status = [];
        state.selectedTickets.assignee = [];
        state.selectedTickets.effortsEstimation = 0;
      } else {
        // Select ticket
        state.selectedTickets.id.push(ticket._id);

        if (!state.selectedTickets.group.includes(groupId)) {
          state.selectedTickets.group.push(groupId);
        }

        if (!state.selectedTickets.status.some((s) => s === ticket.status)) {
        }

        state.selectedTickets.effortsEstimation +=
          Array.isArray(ticket.effortEstimated) &&
          ticket.effortEstimated.length > 0 &&
          typeof ticket.effortEstimated[0] === "number"
            ? ticket.effortEstimated[0]
            : 0;
      }
    },
    toggleSelectTicketByGroup: (state, action: PayloadAction<TicketGroup>) => {
      const selectedGroup = action.payload;
      const groupId = selectedGroup._id;
      const tickets = selectedGroup.ticketsList;

      const isGroupAlreadySelected =
        state.selectedTickets.group.includes(groupId);

      if (isGroupAlreadySelected) {
        state.selectedTickets.group = state.selectedTickets.group.filter(
          (id) => id !== groupId,
        );
        const groupTicketIds = tickets.map((t) => t._id);
        state.selectedTickets.id = state.selectedTickets.id.filter(
          (id) => !groupTicketIds.includes(id),
        );
        const remainingTickets = state.selectedTickets.id
          .map((id) =>
            [...state.selectedTickets.group]
              .flatMap((gId) =>
                gId === selectedGroup._id ? [] : selectedGroup.ticketsList,
              )
              .find((t) => t._id === id),
          )
          .filter(Boolean) as typeof tickets;
        state.selectedTickets.status = [];
        state.selectedTickets.assignee = [];
        state.selectedTickets.effortsEstimation = 0;

        remainingTickets.forEach((ticket) => {
          if (
            ticket.status &&
            !state.selectedTickets.status.some((s) => s === ticket.status)
          ) {
          }
        });
      } else {
        // Select group
        state.selectedTickets.group.push(groupId);

        // Add ticket IDs uniquely
        tickets.forEach((ticket) => {
          if (!state.selectedTickets.id.includes(ticket._id)) {
            state.selectedTickets.id.push(ticket._id);
          }

          // Add unique statuses
          if (
            ticket.status &&
            !state.selectedTickets.status.some((s) => s === ticket.status)
          ) {
          }
        });
      }
    },
    resetSelectedTickets: (state) => {
      state.selectedTickets = { ...defaultSelectedTickets };
    },
    fetchBoardsHomePage: (state) => {
      state.isLoading = true;
    },
    setBoardsHomePage: (
      state,
      action: PayloadAction<{
        favorites: Board[];
        qaBoards: Board[];
        recent: Board[];
        boardsData: Board[];
      }>,
    ) => {
      state.favorites = action.payload.favorites;
      state.qaBoards = action.payload.qaBoards;
      state.recent = action.payload.recent;
      state.boardsData = action.payload.boardsData;
    },

    setSortByColumn: (state, action: PayloadAction<string>) => {
      state.sortByColumn = action.payload;
    },
    setSortByOrder: (state, action: PayloadAction<string>) => {
      state.sortByOrder = action.payload as sortOrder;
    },
    setSortedBoardsData: (state) => {
      const sortedData = [...state.boardsData].sort((a, b) => {
        let valA = a[state.sortByColumn]
          ? a[state.sortByColumn].toString().toLowerCase()
          : "";
        let valB = b[state.sortByColumn]
          ? b[state.sortByColumn].toString().toLowerCase()
          : "";

        if (valA < valB) return state.sortByOrder === sortOrder.ASC ? -1 : 1;
        if (valA > valB) return state.sortByOrder === sortOrder.ASC ? 1 : -1;
        return 0;
      });
      state.boardsData = sortedData;
    },
    renameBoardName: (
      state,
      action: PayloadAction<{ id: string | undefined; name: string }>,
    ) => {
      const boardIndex = state.boardsData.findIndex(
        (board) => board._id === action.payload.id,
      );
      state.boardsData[boardIndex].name = action.payload.name;
      const recentIndex = state.recent.findIndex(
        (board) => board._id === action.payload.id,
      );
      if (recentIndex !== -1) {
        state.recent[recentIndex].name = action.payload.name;
      }
      const favoritesIndex = state.favorites.findIndex(
        (board) => board._id === action.payload.id,
      );
      if (favoritesIndex !== -1) {
        state.favorites[favoritesIndex].name = action.payload.name;
      }
      const qaIndex = state.qaBoards.findIndex(
        (board) => board._id === action.payload.id,
      );
      if (qaIndex !== -1) {
        state.qaBoards[qaIndex].name = action.payload.name;
      }
    },

    boardTabClose: (
      state,
      action: PayloadAction<{ id: string; callback: () => void }>,
    ) => {},
    setSelectedBoardData: (
      state,
      action: PayloadAction<{
        boardId: string;
        projectData: IWorkspaceProjects;
      }>,
    ) => {
      const { boardId, projectData } = action.payload;
      const selectedBoard = state.boardsData?.filter(
        (board) => board._id === boardId,
      );
    },
    addFavoriteBoard: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const boardIndex = state.boardsData?.findIndex(
        (board) => board._id === id,
      );
      if (boardIndex !== -1) {
        state.boardsData[boardIndex].favorite =
          state.boardsData[boardIndex].favorite === 1 ? 0 : 1;
      }

      if (state.selectedBoard.length && state.selectedBoard[0]?._id === id) {
        state.selectedBoard[0].settings.favorite =
          state.selectedBoard[0].settings?.favorite === 1 ? 0 : 1;
      }
      state.favorites = state.boardsData.filter(
        (board) => board.favorite === 1,
      );
    },
    duplicateBoard: (state, action: PayloadAction<{ id: string }>) => {
      const boardIndex = state.boardsData.findIndex(
        (board) => board._id === action.payload.id,
      );
      const newBoard = {
        ...state.boardsData[boardIndex],
        name: `Copy of ${state.boardsData[boardIndex].name}`,
      };
      state.boardsData.push(newBoard);
    },

    archiveBoard: (state, action: PayloadAction<string>) => {
      const boardId = action.payload;
      const filterBoardData = state.boardsData.filter(
        (board) => board._id !== boardId,
      );
      const filterFavoritesData = state.favorites.filter(
        (board) => board._id !== boardId,
      );
      const filterQaBoardsData = state.qaBoards.filter(
        (board) => board._id !== boardId,
      );
      const filterRecentData = state.recent.filter(
        (board) => board._id !== boardId,
      );
      state.boardsData = filterBoardData;
      state.favorites = filterFavoritesData;
      state.qaBoards = filterQaBoardsData;
      state.recent = filterRecentData;
    },

    // Ticket Description related reducers --
    setEnableTicketDescription(state, action: PayloadAction<boolean>) {
      state.enableTicketDescription = action.payload;
    },

    setEnableAddNewTemplateForm(state, action: PayloadAction<boolean>) {
      state.enableAddNewTemplateForm = action.payload;
    },
  },
});

export const { actions, reducer: boardsReducer, name: boardsSlice } = formSlice;
